import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, setBlockTracking as _setBlockTracking, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "card" };
const _hoisted_2 = {
    key: 0,
    class: "px-3 lg:px-4 pt-3 pb-2"
};
const _hoisted_3 = { class: "flex justify-between items-center" };
const _hoisted_4 = { class: "font-semibold" };
const _hoisted_5 = ["aria-label", "title"];
const _hoisted_6 = { class: "my-1 text-muted collapsible-content" };
const _hoisted_7 = { class: "w-full relative aspect-4/3 sm:aspect-video lg:aspect-2/1" };
const _hoisted_8 = {
    key: 0,
    class: "absolute inset-0 flex justify-center items-center text-muted z-2"
};
import { ref, watch } from 'vue';
import { useDeferredChart } from '@ts/common/useDeferredChart';
import { ___, toFixedNumberStr } from '@ts/common/language';
import { kcalToG } from '@ts/common/nutrition';
import { resizeArray } from '@ts/common/array';
import SvgVue from '@ts/components/utils/SvgVue.vue';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import {} from '@ts/common/datasets';
import {} from 'chart.js/auto';
import {} from '@ts/common/chart';
export default /*@__PURE__*/ _defineComponent({
    __name: 'CompareChartMetabolism',
    props: {
        dataA: { default: undefined },
        dataB: { default: undefined },
        help: { type: Boolean, default: false },
        header: { type: Boolean, default: true },
        tooltip: { type: Boolean, default: true },
        overrideAnnotations: { type: Boolean, default: false },
        annotations: { default: [] }
    },
    setup(__props) {
        Chart.register(annotationPlugin);
        const props = __props;
        const showHelp = ref(false);
        const showPlaceholder = ref(true);
        const chart = ref(null);
        const containerRef = ref(null);
        let chartObj = undefined;
        // Create the chart only once its container has a real (non-zero) size — see useDeferredChart.
        useDeferredChart(containerRef, {
            create: () => {
                if (props.dataA !== undefined && props.dataB !== undefined)
                    drawChart(props.dataA, props.dataB);
            },
            getChart: () => chartObj,
        });
        watch(() => [props.dataA, props.dataB], ([newA, newB], [oldA, oldB]) => {
            if (newA !== undefined && newB !== undefined)
                drawChart(newA, newB);
        }, { deep: true });
        function drawChart(dataA, dataB) {
            if (chart.value === null)
                return;
            const maxPower = Math.max(Math.ceil(dataA.chart_metabolism.power_at_zero_fat / 10) * 10, Math.ceil(dataB.chart_metabolism.power_at_zero_fat / 10) * 10);
            const powers = [...Array(maxPower + 1).keys()];
            const energyFatA = resizeArray(dataA.chart_metabolism.energy_fat_per_hour.slice(0, dataA.chart_metabolism.power_at_zero_fat + 1), powers.length);
            const energyFatB = resizeArray(dataB.chart_metabolism.energy_fat_per_hour.slice(0, dataB.chart_metabolism.power_at_zero_fat + 1), powers.length);
            const energyCarboA = resizeArray(dataA.chart_metabolism.energy_carbo_per_hour.slice(0, dataA.chart_metabolism.power_at_zero_fat + 1), powers.length); //.map((element) => (element === 0 ? null : element))
            const energyCarboB = resizeArray(dataB.chart_metabolism.energy_carbo_per_hour.slice(0, dataB.chart_metabolism.power_at_zero_fat + 1), powers.length); //.map((element) => (element === 0 ? null : element))
            const chartData = {
                datasets: [
                    {
                        data: energyFatA.map((value, index) => {
                            return { x: powers[index], y: value };
                        }),
                        label: ___({ en: 'Fat', de: 'Fett' }),
                        unit: ___({
                            en: 'kcal/h',
                            de: 'kcal/h',
                        }),
                        tooltipDigits: 0,
                        borderColor: '#2CB7C1',
                        borderWidth: 2,
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        yAxisID: 'y1',
                    },
                    {
                        data: energyFatB.map((value, index) => {
                            return { x: powers[index], y: value };
                        }),
                        label: ___({ en: 'Fat (comp.)', de: 'Fett (vergl.)' }),
                        unit: ___({
                            en: 'kcal/h',
                            de: 'kcal/h',
                        }),
                        tooltipDigits: 0,
                        borderColor: '#2CB7C1',
                        borderWidth: 2,
                        borderDash: [10, 5],
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        yAxisID: 'y1',
                    },
                    {
                        data: energyCarboA.map((value, index) => {
                            return { x: powers[index], y: value };
                        }),
                        label: ___({
                            en: 'Carbohydrates',
                            de: 'Kohlenhydrate',
                        }),
                        unit: ___({
                            en: 'kcal/h',
                            de: 'kcal/h',
                        }),
                        tooltipDigits: 0,
                        borderColor: '#CDB663',
                        borderWidth: 2,
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        yAxisID: 'y',
                    },
                    {
                        data: energyCarboB.map((value, index) => {
                            return { x: powers[index], y: value };
                        }),
                        label: ___({
                            en: 'Carbohydrates (comp.)',
                            de: 'Kohlenhydrate (vergl.)',
                        }),
                        unit: ___({
                            en: 'kcal/h',
                            de: 'kcal/h',
                        }),
                        tooltipDigits: 0,
                        borderColor: '#CDB663',
                        borderWidth: 2,
                        borderDash: [10, 5],
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        yAxisID: 'y',
                    },
                ],
            };
            const chartOptions = {
                normalized: true,
                parsing: false,
                maintainAspectRatio: false,
                responsive: true,
                animation: false,
                events: props.tooltip ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] : [],
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: ___({
                                en: 'Power [W]',
                                de: 'Leistung [W]',
                            }),
                        },
                        max: maxPower,
                        type: 'linear',
                        min: dataA.chart_metabolism.power[0],
                        ticks: {
                            autoSkip: true,
                        },
                    },
                    y: {
                        // primary axis — carbohydrates (the limiting/important fuel)
                        type: 'linear',
                        position: 'left',
                        title: {
                            display: true,
                            text: ___({
                                en: 'Energy consumption carbohydrates [kcal/h]',
                                de: 'Energieverbrauch Kohlenhydrate [kcal/h]',
                            }),
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                        max: Math.min(Math.floor(dataA.chart_metabolism.energy_carbo_per_hour_plot_limit / 10) * 10, Math.floor(dataB.chart_metabolism.energy_carbo_per_hour_plot_limit / 10) * 10),
                        min: 0,
                        ticks: {
                            autoSkip: true,
                        },
                    },
                    y1: {
                        // secondary axis — fat (autoscaled)
                        type: 'linear',
                        position: 'right',
                        title: {
                            display: true,
                            text: ___({
                                en: 'Energy consumption fat [kcal/h]',
                                de: 'Energieverbrauch Fett [kcal/h]',
                            }),
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                        min: 0,
                        ticks: {
                            autoSkip: true,
                        },
                    },
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false,
                },
                plugins: {
                    ...(props.tooltip
                        ? {
                            tooltip: {
                                callbacks: {
                                    title: (context) => {
                                        return ___({
                                            en: 'At ' + context[0].label + ' W:',
                                            de: 'Bei ' + context[0].label + ' W:',
                                        });
                                    },
                                    label: (context) => {
                                        // datasets 0,1 = fat (A,B); datasets 2,3 = carbohydrates (A,B)
                                        const kcal = context.raw.y;
                                        const substrate = context.datasetIndex === 2 || context.datasetIndex === 3 ? 'carb' : 'fat';
                                        return context.dataset.label + ': ' + toFixedNumberStr(kcalToG(kcal, substrate), 1) + ' g/h ⇔' + toFixedNumberStr(kcal, 0) + ' kcal/h';
                                    },
                                    labelColor: (context) => {
                                        return {
                                            backgroundColor: 'rgba(0, 0, 0, 0)',
                                            borderColor: context.dataset.borderColor ?? 'rgba(0, 0, 0, 0)',
                                            borderWidth: 2,
                                            borderDash: context.dataset.borderDash ?? [10, 5],
                                        };
                                    },
                                },
                            },
                        }
                        : {
                            tooltip: {
                                enabled: false,
                            },
                        }),
                    annotation: {
                        common: {
                            drawTime: 'afterDatasetsDraw',
                        },
                        annotations: [
                            ...(props.overrideAnnotations
                                ? []
                                : [
                                    {
                                        // drawTime: "afterDatasetsDraw",
                                        id: 'hline1',
                                        type: 'line',
                                        mode: 'vertical',
                                        scaleID: 'x',
                                        value: dataA.fatmax_abs,
                                        borderColor: '#2cb7c0',
                                        borderDashOffset: 5,
                                        borderWidth: 2,
                                    },
                                    {
                                        id: 'hline2',
                                        type: 'line',
                                        mode: 'vertical',
                                        scaleID: 'x',
                                        value: dataB.fatmax_abs,
                                        borderColor: '#2cb7c0',
                                        borderDash: [10, 5],
                                        borderDashOffset: 5,
                                        borderWidth: 2,
                                    },
                                    {
                                        id: 'hlineLabel1',
                                        type: 'line',
                                        mode: 'vertical',
                                        scaleID: 'x',
                                        value: dataA.fatmax_abs,
                                        borderWidth: 0,
                                        label: {
                                            position: 'end',
                                            backgroundColor: '#2cb7c0',
                                            content: 'Fatmax',
                                            display: true,
                                            xAdjust: dataA.fatmax_abs > dataB.fatmax_abs ? 15 : -15,
                                            yAdjust: -30,
                                        },
                                    },
                                    {
                                        id: 'hlineLabel2',
                                        type: 'line',
                                        mode: 'vertical',
                                        scaleID: 'x',
                                        value: dataB.fatmax_abs,
                                        borderWidth: 0,
                                        label: {
                                            position: 'end',
                                            backgroundColor: '#2cb7c0',
                                            content: [
                                                ___({
                                                    en: 'comp.',
                                                    de: 'vergl.',
                                                }),
                                            ],
                                            display: true,
                                            xAdjust: dataA.fatmax_abs > dataB.fatmax_abs ? -10 : 10,
                                        },
                                    },
                                ]),
                            ...props.annotations,
                        ],
                    },
                },
            };
            if (chartObj === undefined) {
                chartObj = new Chart(chart.value, {
                    type: 'line',
                    data: chartData,
                    options: chartOptions,
                });
                showPlaceholder.value = false;
            }
            else {
                chartObj.data = chartData;
                chartObj.options = chartOptions;
                chartObj.update();
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                _createElementVNode("div", _hoisted_1, [
                    (__props.header)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(___)({ en: 'Energy Consumption', de: 'Energieverbrauch' })), 1 /* TEXT */),
                                (__props.help)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        onClick: _cache[1] || (_cache[1] = ($event) => (showHelp.value = !showHelp.value)),
                                        type: "button",
                                        class: "btn text-gray-350 dark:text-gray-450 hover:text-primary-500",
                                        "aria-label": _unref(___)({ en: 'Toggle help', de: 'Hilfe ein- und ausblenden' }),
                                        title: _unref(___)({ en: 'Help', de: 'Hilfe' })
                                    }, [
                                        _cache[0] || (_setBlockTracking(-1, true),
                                            (_cache[0] = _createVNode(SvgVue, {
                                                class: "w-4 h-4",
                                                icon: "lucide/circle-question-mark"
                                            })).cacheIndex = 0,
                                            _setBlockTracking(1),
                                            _cache[0])
                                    ], 8 /* PROPS */, _hoisted_5))
                                    : _createCommentVNode("v-if", true)
                            ]),
                            (__props.help)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: _normalizeClass(["collapsible", showHelp.value ? 'expanded' : ''])
                                }, [
                                    _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(___)({
                                        en: 'This graph shows the different composition of the energy carriers fats and carbohydrates over the course of the performance, as well as the maximum rate of fat combustion - Fatmax.',
                                        de: 'Diese Grafik zeigt die unterschiedliche Zusammensetzung der Energieträger Fette und Kohlenhydrate über den Verlauf der Leistung, sowie die maximale Fettverbrennung Fatmax.',
                                    })), 1 /* TEXT */)
                                ], 2 /* CLASS */))
                                : _createCommentVNode("v-if", true)
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("div", {
                            class: "absolute inset-0",
                            ref_key: "containerRef",
                            ref: containerRef
                        }, [
                            _createElementVNode("canvas", {
                                class: "w-full h-full",
                                ref_key: "chart",
                                ref: chart
                            }, null, 512 /* NEED_PATCH */)
                        ], 512 /* NEED_PATCH */),
                        (showPlaceholder.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                _createElementVNode("div", null, [
                                    _cache[2] || (_setBlockTracking(-1, true),
                                        (_cache[2] = _createVNode(SvgVue, {
                                            class: "w-4 h-4",
                                            icon: "lucide/chart-line"
                                        })).cacheIndex = 2,
                                        _setBlockTracking(1),
                                        _cache[2]),
                                    _createTextVNode(" " + _toDisplayString(_unref(___)({ en: 'loading...', de: 'lädt...' })), 1 /* TEXT */)
                                ])
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ])
            ]));
        };
    }
});
